/*
 * File: ContactItem.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

interface ContactItemProps {
  /**
   * The type of contact (phone or email)
   */
  type: 'phone' | 'email';

  /**
   * The contact value (phone number or email address)
   */
  value: string;

  /**
   * Optional label for the contact (e.g., "Cell", "Home", "Work")
   */
  label?: string;

  /**
   * Optional extension for work phone numbers
   */
  extension?: string | null;
}

/**
 * A single contact item (phone or email) with appropriate icon and link
 */
export default function ContactItem({ type, value, label, extension }: ContactItemProps) {
  const Icon = type === 'phone' ? PhoneIcon : EmailIcon;
  const href = type === 'phone' ? `tel:${value}${extension ? ',' + extension : ''}` : `mailto:${value}`;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Icon sx={{ fontSize: 16 }} color="action" />
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 38 }}>
          {label}:
        </Typography>
      )}
      <Link href={href} underline="hover">
        <Typography variant="body2">
          {value}
          {extension && ` ext. ${extension}`}
        </Typography>
      </Link>
    </Box>
  );
}
